import React, { useEffect, useState } from 'react';
import {Box, CircularProgress, TableCell, Tooltip} from '@mui/material';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import ItemListDialog, { ItemListDataItem } from './item-list-dialog';
import DataGrid from '../../components/grid/dataGrid';
import GlobalInput from '../../components/global/input';
import GlobalButton from '../../components/global/button';
import FilterContainer from '../../components/layout/filterContainer';
import { CustomTableCheckboxCell } from '../../components/grid/CustomTableCell';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus-icon.svg';
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh-icon.svg';
import { ReactComponent as FilterIcon } from '../../assets/icons/filter-icon.svg';

import { useAppSelector } from '../../hooks/hooks';
import EventApi from '../../api/event.api';
import ContentContainer from "../../components/layout/contentContainer";
import GlobalDropdown from "../../components/global/dropdown";

const ItemList = () => {

    const eventApi = new EventApi();
    // const appState = useAppSelector((state) => state.appState);

    const defaultFilterObject = {
        category: null,
        name: null,
        description: null
    }

    const [state, setState] = useState({
        itemListDialog: {
            isOpen: false,
            data: null,
        },
        itemCategories: [],
        filterObject: defaultFilterObject,
        items: [],
        showLoading: false,
    });

    // const { viewType, campus } = appState;

    useEffect(() => {

        const fetchData = async () => {
            setState(prevState => ({ ...prevState, showLoading: true }));
            try {
                const itemList = await eventApi.GetItems();
                const itemCategories = await eventApi.GetItemsCategories();
                setState(prevState => ({
                    ...prevState,
                    itemCategories: itemCategories,
                    items: itemList,
                }));
            } finally {
                setState(prevState => ({ ...prevState, showLoading: false }));
            }
        };

        fetchData();

    }, []);

    const handleFilterObjChange = (name: any, value: any) => {
        if (value === '') value = null;

        setState(prevState => ({
            ...prevState,
            filterObject: {
                ...prevState.filterObject,
                [name]: value,
            },
        }));
    };

    const handleClearAllFilters = () => {
        setState(prevState => ({
            ...prevState,
            filterObject: defaultFilterObject,
        }));
    };

    const handleAddNewItem = () => {

        const data = {
            id: null,
            name: "",
            description: "",
            costPerItem: 0,
            gstRate: 0,
            pstRate: 0,
            isActive: true,
            isPriceAdjustmentAllowed: true,
            itemCategory: {
                id: null,
                name: "",
                code: ""
            }
        }

        handleItemListDialog(data);

    };

    const handleItemListDialog = (data: any = null) => {
        setState(prevState => ({
            ...prevState,
            itemListDialog: {
                isOpen: !state.itemListDialog.isOpen,
                data: data,
            },
        }));
    }

    const addItem = async (item: ItemListDataItem) => {
        setState(prevState => ({ ...prevState, showLoading: true }));
        try {
            await eventApi.CreateItem(item);
            const itemList = await eventApi.GetItems();
            handleItemListDialog();
            setState(prevState => ({
                ...prevState,
                items: itemList,
            }))
        } catch (error) {
            toast.error('Error adding item');
        } finally {
            setState(prevState => ({ ...prevState, showLoading: false }));
        }
    }

    const saveItem = async (item: ItemListDataItem) => {
        setState(prevState => ({ ...prevState, showLoading: true }));
        try {
            await eventApi.UpdateItem(item);
            const itemList = await eventApi.GetItems();
            handleItemListDialog();
            setState(prevState => ({
                ...prevState,
                items: itemList,
            }))
        } catch (error) {
            toast.error('Error saving item');
        } finally {
            setState(prevState => ({ ...prevState, showLoading: false }));
        }
    }

    const itemListGridColumns = [
        {
            id: 'itemCategory',
            label: 'Category',
            objectId: 'name',
        },
        {
            id: 'name',
            label: 'Item',
        },
        {
            id: 'description',
            label: 'Description',
            disabled: true
        },
        {
            id: 'costPerItem',
            label: 'Price',
        },
        {
            id: 'gstRate',
            label: 'GST',
            disabled: true
        },
        {
            id: 'pstRate',
            label: 'PST',
            disabled: true
        },
        {
            id: 'isActive',
            label: 'Active',
            sx: {
                textAlign: 'center',
            },
            disabled: true,
        },
    ];

    const renderGridColumns = (data: any, onChange: any) => {
        const { name: categoryName } = data?.itemCategory;
        const { name, description, costPerItem, gstRate, pstRate } = data;

        return (
            <>
                <TableCell sx={{ width: '9%' }}>{categoryName}</TableCell>
                <Tooltip
                    title={name}
                    arrow
                    placement="top"
                    PopperProps={{
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, -15],
                                },
                            },
                        ],
                    }}
                >
                    <TableCell sx={{ maxWidth:'7rem', width: '20%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</TableCell>
                </Tooltip>
                <Tooltip
                    title={description}
                    arrow
                    placement="top"
                    PopperProps={{
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, -15],
                                },
                            },
                        ],
                    }}
                >
                    <TableCell sx={{ maxWidth:'6rem', width: '15%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{description}</TableCell>
                </Tooltip>
                <TableCell sx={{ width: '10%' }}>${costPerItem}</TableCell>
                <TableCell sx={{ width: '5%' }}>{gstRate}%</TableCell>
                <TableCell sx={{ width: '5%' }}>{pstRate}%</TableCell>
                <CustomTableCheckboxCell {...{
                    data,
                    name: 'isActive',
                    onChange,
                    disabled: false,
                    width: '9%',
                }} />
            </>
        );
    };


    const getGridData = () => {

        const { category, name, description } = state.filterObject;

        let filteredData = [...state.items];

        if (category) filteredData = filteredData.filter((d) => d.itemCategory.name.toLowerCase().includes(category.name.toLowerCase()));
        if (name) filteredData = filteredData.filter((d) => d.name.toLowerCase().includes(name.toLowerCase()));
        if (description) filteredData = filteredData.filter((d) => d.description.toLowerCase().includes(description.toLowerCase()));

        return filteredData;
    };

    return <>
        <FilterContainer sx={{ flexDirection: 'column', p: '7px' }}>

            <FlexBox sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <FlexBox>
                    <GlobalDropdown
                        label={'Category'}
                        placeholder={'Select Category'}
                        data={state.itemCategories}
                        field={'id'}
                        name={'name'}
                        dataItemKey={'category'}
                        value={state.filterObject?.category}
                        onChange={handleFilterObjChange}
                        disabled={state.showLoading}
                    />
                    <GlobalInput icon={<FilterIcon/>} label={"filter"} placeholder="Name" name={'name'} value={state.filterObject?.name} onChange={handleFilterObjChange} disabled={state.showLoading} />
                    <GlobalInput placeholder="Description" name={'description'} value={state.filterObject?.description} onChange={handleFilterObjChange} disabled={state.showLoading} />
                    <GlobalButton variant="round" text="clear all" secondary={'true'} endIcon={<RefreshIcon />} onClick={handleClearAllFilters} disabled={state.showLoading} />

                </FlexBox>
                <FlexBox sx={{
                    mt: '10px',
                    width: '10%',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    height: '100%',
                }}>
                    <GlobalButton text="Add Item" variant="round" startIcon={<PlusIcon />} onClick={handleAddNewItem} disabled={state.showLoading} />
                </FlexBox>
            </FlexBox>
        </FilterContainer>

        <ContentContainer>
            {state.showLoading && (
                <FlexBox
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        zIndex: 10,
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <CircularProgress size={100} />
                </FlexBox>
            )}
            <DataGrid
                isEdit
                topHeight={20}
                data={getGridData().sort((a, b) => {
                    if (a.itemCategory.name < b.itemCategory.name) return -1;
                    if (a.itemCategory.name > b.itemCategory.name) return 1;
                    if (a.name < b.name) return -1;
                    if (a.name > b.name) return 1;
                    if (a.costPerItem < b.costPerItem) return -1;
                    if (a.costPerItem > b.costPerItem) return 1;
                })}
                columns={itemListGridColumns}
                dataItemKey="id"
                renderRows={renderGridColumns}
                onEdit={handleItemListDialog}
            />
        </ContentContainer>

        {state.itemListDialog.isOpen && (
            <ItemListDialog
                data={state.itemListDialog?.data}
                itemCategories={state.itemCategories}
                onClose={handleItemListDialog}
                onSave={saveItem}
                onCreate={addItem}
            />
        )}
    </>;
};

export default ItemList;

const FlexBox = styled(Box)`
    && {
        display: flex;

        & > * {
            margin-right: 10px;
        }

        & > :last-child {
            margin-right: 0px;
        }
    }
`;